/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from './lib/queryClient';
import { KYCProvider } from './contexts/KYCContext';
import { AccountFlaggedProvider } from './contexts/AccountFlaggedContext';

import DashboardLayout from './components/layout/DashboardLayout';
import Login from './pages/Login';
import CliAuthorize from './pages/CliAuthorize';
import SSOCallback from './pages/SSOCallback';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Dashboard from './pages/Dashboard';
import Services from './pages/Services';
import Advisor from './pages/Advisor';
import ProjectMembers from './pages/ProjectMembers';
import BillingLayout from './pages/billing/BillingLayout';
import BillingOverview from './pages/billing/BillingOverview';
import BillingTransactions from './pages/billing/BillingTransactions';
import BillingInvoices from './pages/billing/BillingInvoices';
import BillingPayments from './pages/billing/BillingPayments';
import AccessKeysSettings from './pages/settings/AccessKeysSettings';
import Projects from './pages/Projects';
import ProjectOverview from './pages/ProjectOverview';
import ProjectSettings from './pages/ProjectSettings';
import OrganizationSettings from './pages/organization/OrganizationSettings';
import AcceptInvitation from './pages/AcceptInvitation';
import Pods from './pages/Pods';
import PodDetail from './pages/PodDetail';
import CreatePod from './pages/CreatePod';
import VMs from './pages/VMs';
import CreateVM from './pages/CreateVM';
import VMDetail from './pages/VMDetail';
import VMSnapshots from './pages/VMSnapshots';
import VMPools from './pages/VMPools';
import CreateVMPool from './pages/CreateVMPool';
import DataVolumes from './pages/DataVolumes';
import CreateDataVolume from './pages/CreateDataVolume';
import CloudInitTemplates from './pages/CloudInitTemplates';
import Storage from './pages/Storage';
import ManagedMLOpsCatalog from './pages/managed/ManagedMLOpsCatalog';
import ManagedInstances from './pages/managed/ManagedInstances';
import ManagedInstanceDetail from './pages/managed/ManagedInstanceDetail';
import FinopsDashboard from './pages/finops/FinopsDashboard';
import FinopsConnections from './pages/finops/Connections';
import FinopsRecommendations from './pages/finops/Recommendations';
import PodstackDrive from './pages/PodstackDrive';
import Pricing from './pages/Pricing';
import Volumes from './pages/Volumes';
import KYC from './pages/KYC';
import Templates from './pages/Templates';
import AuditLogs from './pages/AuditLogs';
import SettingsLayout from './components/settings/SettingsLayout';
import ProfileSettings from './pages/settings/ProfileSettings';
import SecuritySettings from './pages/settings/SecuritySettings';
import NotificationSettings from './pages/settings/NotificationSettings';
import AppearanceSettings from './pages/settings/AppearanceSettings';
import QuickNotes from './pages/QuickNotes';
import TerminalWindow from './pages/TerminalWindow';
import AssignedResources from './pages/AssignedResources';
import AssignedPodDetail from './pages/AssignedPodDetail';
import AssignedHelmDetail from './pages/AssignedHelmDetail';
import BaremetalMarketplace from './pages/baremetal/Marketplace';
import BaremetalInstances from './pages/baremetal/Instances';
import BaremetalReservations from './pages/baremetal/Reservations';
import BaremetalCreateOrder from './pages/baremetal/CreateOrder';
import BaremetalCheckout from './pages/baremetal/Checkout';
import BaremetalOrderStatus from './pages/baremetal/OrderStatus';
import { ExperimentTracking, ExperimentDetail, RunDetail, ModelRegistry, Schedules, Approvals, PipelineList, PipelineBuilder, PipelineDetail, PipelineRunDetail, MonitoringDashboard, MonitorDetail, MonitorAlerts, DriftDashboard, DriftMonitorDetail, DriftReportDetail } from './pages/registry';
import { isVMManagerEnabled, isAIStudioEnabled, isMLOpsEnabled, isExperimentsEnabled, isModelRegistryEnabled, isQuickNotesEnabled, isInferenceEnabled, isReactiveNotebooksEnabled, isMLPipelinesEnabled, isModelMonitoringEnabled, isDriftDetectionEnabled, isSandboxEnabled, isKYCEnforcementEnabled, isPodvirtInferenceEnabled, isFileStorageEnabled, isBaremetalEnabled, isManagedMLOpsEnabled, isFinOpsEnabled } from './config/featureFlags';

// Lazy load AI Studio components only if feature is enabled
const ModelCatalogue = isAIStudioEnabled()
  ? React.lazy(() => import('./pages/ai-studio/models/ModelCatalogue'))
  : null;
const ModelPlayground = isAIStudioEnabled()
  ? React.lazy(() => import('./pages/ai-studio/models/ModelPlayground'))
  : null;
const Deployments = isAIStudioEnabled()
  ? React.lazy(() => import('./pages/ai-studio/deployments/Deployments'))
  : null;
const DeployModelPage = isAIStudioEnabled()
  ? React.lazy(() => import('./pages/ai-studio/deployments/DeployModel'))
  : null;
const Finetuning = isAIStudioEnabled()
  ? React.lazy(() => import('./pages/ai-studio/finetuning/Finetuning'))
  : null;
const Evaluation = isAIStudioEnabled()
  ? React.lazy(() => import('./pages/ai-studio/evaluation/Evaluation'))
  : null;
const AIStudioApiKeysPage = isAIStudioEnabled()
  ? React.lazy(() => import('./pages/ai-studio/AIStudioApiKeys'))
  : null;

// Lazy load Reactive Notebook components if feature is enabled
const NotebooksListPage = isReactiveNotebooksEnabled()
  ? React.lazy(() => import('./features/notebook/components/NotebooksListPage'))
  : null;
const NotebookApp = isReactiveNotebooksEnabled()
  ? React.lazy(() => import('./features/notebook/components/NotebookApp'))
  : null;
const PodTemplatesPage = isReactiveNotebooksEnabled()
  ? React.lazy(() => import('./features/notebook/components/PodTemplates'))
  : null;
// Image-as-code build history (podstack.Image.build()). Scoped behind the
// same feature flag as pod templates since both are only reachable from
// the reactive-notebook surface today.
const ImageBuildsPage = isReactiveNotebooksEnabled()
  ? React.lazy(() => import('./features/notebook/components/ImageBuilds'))
  : null;

// Lazy load Inference components only if feature is enabled
const InferenceModelCatalog = isInferenceEnabled()
  ? React.lazy(() => import('./pages/inference/ModelCatalog'))
  : null;
const InferencePlayground = isInferenceEnabled()
  ? React.lazy(() => import('./pages/inference/InferencePlayground'))
  : null;
const InferenceApiKeys = isInferenceEnabled()
  ? React.lazy(() => import('./pages/inference/ApiKeys'))
  : null;

// Lazy load Podvirt Serverless Inference components if feature is enabled
const ServerlessModels = isPodvirtInferenceEnabled()
  ? React.lazy(() => import('./pages/inference/ServerlessModels'))
  : null;
const ServerlessChat = isPodvirtInferenceEnabled()
  ? React.lazy(() => import('./pages/inference/ServerlessChat'))
  : null;
const ServerlessVideo = isPodvirtInferenceEnabled()
  ? React.lazy(() => import('./pages/inference/ServerlessVideo'))
  : null;
const GpuDashboard = isPodvirtInferenceEnabled()
  ? React.lazy(() => import('./pages/inference/GpuDashboard'))
  : null;

// Lazy load Sandbox components only if feature is enabled
const Sandboxes = isSandboxEnabled()
  ? React.lazy(() => import('./pages/Sandboxes'))
  : null;
const CreateSandbox = isSandboxEnabled()
  ? React.lazy(() => import('./pages/CreateSandbox'))
  : null;
const SandboxDetail = isSandboxEnabled()
  ? React.lazy(() => import('./pages/SandboxDetail'))
  : null;

/** Nested under /ai-studio/deployments so `new` matches before index (avoids mis-routing to catch-all). */
function AiStudioDeploymentsShell() {
  return <Outlet />;
}

// Redirect that keeps ?query and #hash - payment providers (PayPal/Binance)
// return to /portal/wallet?payment=…&token=…, which must reach /billing intact.
const RedirectKeepSearch: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/portal">
        <AccountFlaggedProvider>
        <KYCProvider disabled={!isKYCEnforcementEnabled()}>
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/cli/authorize" element={<CliAuthorize />} />
          <Route path="/auth/sso/callback" element={<SSOCallback />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          <Route path="/invitations/accept" element={<AcceptInvitation />} />

          {/* Standalone terminal window (fullscreen, no layout) */}
          <Route path="/terminal" element={<TerminalWindow />} />

          {/* Standalone notebook editor (fullscreen, Jupyter-like, no dashboard nav) */}
          {isReactiveNotebooksEnabled() && NotebookApp && (
            <Route
              path="/notebooks/:id"
              element={
                <React.Suspense fallback={<div className="flex items-center justify-center h-screen bg-gray-950"><div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>}>
                  <NotebookApp />
                </React.Suspense>
              }
            />
          )}

          {/* Protected routes */}
          <Route element={<DashboardLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/services" element={<Services />} />
            <Route path="/advisor" element={<Advisor />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/members" element={<ProjectMembers />} />
            <Route path="/projects/audit-log" element={<AuditLogs />} />
            <Route path="/projects/:id" element={<ProjectOverview />} />
            <Route path="/projects/:id/settings" element={<ProjectSettings />} />
            <Route path="/organization/settings" element={<OrganizationSettings />} />
            <Route path="/pods" element={<Pods />} />
            <Route path="/pods/create" element={<CreatePod />} />
            <Route path="/pods/templates" element={<Templates />} />
            <Route path="/pods/:id" element={<PodDetail />} />
            {isVMManagerEnabled() && <Route path="/vms" element={<VMs />} />}
            {isVMManagerEnabled() && <Route path="/vms/create" element={<CreateVM />} />}
            {isVMManagerEnabled() && <Route path="/vms/:id" element={<VMDetail />} />}
            {isVMManagerEnabled() && <Route path="/vms/snapshots" element={<VMSnapshots />} />}
            {isVMManagerEnabled() && <Route path="/vm-pools" element={<VMPools />} />}
            {isVMManagerEnabled() && <Route path="/vm-pools/create" element={<CreateVMPool />} />}
            {isVMManagerEnabled() && <Route path="/data-volumes" element={<DataVolumes />} />}
            {isVMManagerEnabled() && <Route path="/data-volumes/create" element={<CreateDataVolume />} />}
            {isVMManagerEnabled() && <Route path="/cloudinit-templates" element={<CloudInitTemplates />} />}
            {isFileStorageEnabled() && <Route path="/storage" element={<Storage />} />}
            {isManagedMLOpsEnabled() && <Route path="/managed-mlops" element={<ManagedMLOpsCatalog />} />}
            {isManagedMLOpsEnabled() && <Route path="/managed-mlops/instances" element={<ManagedInstances />} />}
            {isManagedMLOpsEnabled() && <Route path="/managed-mlops/instances/:id" element={<ManagedInstanceDetail />} />}
            {isFinOpsEnabled() && <Route path="/finops" element={<FinopsDashboard />} />}
            {isFinOpsEnabled() && <Route path="/finops/connections" element={<FinopsConnections />} />}
            {isFinOpsEnabled() && <Route path="/finops/recommendations" element={<FinopsRecommendations />} />}
            <Route path="/drive" element={<PodstackDrive />} />
            <Route path="/drive/pricing" element={<Pricing />} />
            <Route path="/volumes" element={<Volumes />} />
            <Route path="/templates" element={<RedirectKeepSearch to="/pods/templates" />} />
            <Route path="/ssh-keys" element={<RedirectKeepSearch to="/settings/access-keys" />} />
            {isBaremetalEnabled() && (
              <>
                <Route path="/baremetal/marketplace" element={<BaremetalMarketplace />} />
                <Route path="/baremetal/instances" element={<BaremetalInstances />} />
                <Route path="/baremetal/reservations" element={<BaremetalReservations />} />
                <Route path="/baremetal/create" element={<BaremetalCreateOrder />} />
                <Route path="/baremetal/checkout/:orderId" element={<BaremetalCheckout />} />
                <Route path="/baremetal/status/:orderId" element={<BaremetalOrderStatus />} />
              </>
            )}
            {/* Reactive Notebook list (inside dashboard layout) */}
            {isReactiveNotebooksEnabled() && NotebooksListPage && (
              <Route
                path="/notebooks"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <NotebooksListPage />
                  </React.Suspense>
                }
              />
            )}
            {/* User-defined pod templates for cell execution */}
            {isReactiveNotebooksEnabled() && PodTemplatesPage && (
              <Route
                path="/notebooks/pod-templates"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PodTemplatesPage />
                  </React.Suspense>
                }
              />
            )}
            {/* Image-as-code build history */}
            {isReactiveNotebooksEnabled() && ImageBuildsPage && (
              <Route
                path="/notebooks/image-builds"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ImageBuildsPage />
                  </React.Suspense>
                }
              />
            )}

            {/* Sandbox routes */}
            {isSandboxEnabled() && Sandboxes && (
              <Route
                path="/sandboxes"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <Sandboxes />
                  </React.Suspense>
                }
              />
            )}
            {isSandboxEnabled() && CreateSandbox && (
              <Route
                path="/sandboxes/create"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CreateSandbox />
                  </React.Suspense>
                }
              />
            )}
            {isSandboxEnabled() && SandboxDetail && (
              <Route
                path="/sandboxes/:id"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <SandboxDetail />
                  </React.Suspense>
                }
              />
            )}

            <Route path="/billing" element={<BillingLayout />}>
              <Route index element={<BillingOverview />} />
              <Route path="transactions" element={<BillingTransactions />} />
              <Route path="invoices" element={<BillingInvoices />} />
              <Route path="payments" element={<BillingPayments />} />
            </Route>
            <Route path="/wallet" element={<RedirectKeepSearch to="/billing" />} />
            <Route path="/invoices" element={<RedirectKeepSearch to="/billing/invoices" />} />
            {isKYCEnforcementEnabled() && <Route path="/kyc" element={<KYC />} />}
            {/* Settings routes with nested layout */}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/settings/access-keys" replace />} />
              <Route path="profile" element={<ProfileSettings />} />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="access-keys" element={<AccessKeysSettings />} />
              <Route path="tokens" element={<Navigate to="/settings/access-keys" replace />} />
              <Route path="notifications" element={<NotificationSettings />} />
              <Route path="appearance" element={<AppearanceSettings />} />
            </Route>
            <Route path="/audit-logs" element={<RedirectKeepSearch to="/projects/audit-log" />} />
            <Route path="/assignments" element={<AssignedResources />} />
            <Route path="/assignments/pods/:id" element={<AssignedPodDetail />} />
            <Route path="/assignments/helm/:id" element={<AssignedHelmDetail />} />
            {isQuickNotesEnabled() && <Route path="/quick-notes" element={<QuickNotes />} />}

            {/* MLOps routes - only register if feature is enabled */}
            {isMLOpsEnabled() && isExperimentsEnabled() && (
              <>
                <Route path="/registry/experiments" element={<ExperimentTracking />} />
                <Route path="/registry/experiments/:id" element={<ExperimentDetail />} />
                <Route path="/registry/runs/:id" element={<RunDetail />} />
              </>
            )}
            {isMLOpsEnabled() && isModelRegistryEnabled() && (
              <Route path="/registry/models" element={<ModelRegistry />} />
            )}
            {isMLOpsEnabled() && (
              <>
                <Route path="/registry/schedules" element={<Schedules />} />
                <Route path="/registry/approvals" element={<Approvals />} />
              </>
            )}

            {/* ML Pipeline Orchestration routes */}
            {isMLOpsEnabled() && isMLPipelinesEnabled() && (
              <>
                <Route path="/registry/pipelines" element={<PipelineList />} />
                <Route path="/registry/pipelines/create" element={<PipelineBuilder />} />
                <Route path="/registry/pipelines/:id" element={<PipelineDetail />} />
                <Route path="/registry/pipelines/:id/edit" element={<PipelineBuilder />} />
                <Route path="/registry/pipeline-runs/:runId" element={<PipelineRunDetail />} />
              </>
            )}

            {/* Model Monitoring routes */}
            {isMLOpsEnabled() && isModelMonitoringEnabled() && (
              <>
                <Route path="/registry/monitoring" element={<MonitoringDashboard />} />
                <Route path="/registry/monitoring/:id" element={<MonitorDetail />} />
                <Route path="/registry/monitoring/:id/alerts" element={<MonitorAlerts />} />
              </>
            )}

            {/* Drift Detection routes */}
            {isMLOpsEnabled() && isDriftDetectionEnabled() && (
              <>
                <Route path="/registry/drift" element={<DriftDashboard />} />
                <Route path="/registry/drift/:id" element={<DriftMonitorDetail />} />
                <Route path="/registry/drift/:id/reports/:reportId" element={<DriftReportDetail />} />
              </>
            )}

            {/* AI Studio routes - only register if feature is enabled */}
            {isAIStudioEnabled() && ModelCatalogue && (
              <Route
                path="/ai-studio/models"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ModelCatalogue />
                  </React.Suspense>
                }
              />
            )}
            {isAIStudioEnabled() && ModelPlayground && (
              <Route
                path="/ai-studio/models/playground/:modelId"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ModelPlayground />
                  </React.Suspense>
                }
              />
            )}
            {isAIStudioEnabled() && Finetuning && (
              <Route
                path="/ai-studio/finetuning"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <Finetuning />
                  </React.Suspense>
                }
              />
            )}
            {isAIStudioEnabled() && Deployments && DeployModelPage && (
              <Route path="/ai-studio/deployments" element={<AiStudioDeploymentsShell />}>
                <Route
                  index
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <Deployments />
                    </React.Suspense>
                  }
                />
                <Route
                  path="new"
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <DeployModelPage />
                    </React.Suspense>
                  }
                />
              </Route>
            )}
            {isAIStudioEnabled() && Evaluation && (
              <Route
                path="/ai-studio/evaluation"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <Evaluation />
                  </React.Suspense>
                }
              />
            )}
            {isAIStudioEnabled() && AIStudioApiKeysPage && (
              <Route
                path="/ai-studio/api-keys"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AIStudioApiKeysPage />
                  </React.Suspense>
                }
              />
            )}

            {/* Inference routes - only register if feature is enabled */}
            {isInferenceEnabled() && InferenceModelCatalog && (
              <Route
                path="/inference/catalog"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <InferenceModelCatalog />
                  </React.Suspense>
                }
              />
            )}
            {isInferenceEnabled() && InferenceApiKeys && (
              <Route
                path="/inference/api-keys"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <InferenceApiKeys />
                  </React.Suspense>
                }
              />
            )}
            {isInferenceEnabled() && InferencePlayground && (
              <Route
                path="/inference/playground"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <InferencePlayground />
                  </React.Suspense>
                }
              />
            )}

            {/* Podvirt Serverless Inference routes */}
            {isPodvirtInferenceEnabled() && ServerlessModels && (
              <Route
                path="/inference/serverless"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ServerlessModels />
                  </React.Suspense>
                }
              />
            )}
            {isPodvirtInferenceEnabled() && ServerlessChat && (
              <Route
                path="/inference/serverless/:id/chat"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ServerlessChat />
                  </React.Suspense>
                }
              />
            )}
            {isPodvirtInferenceEnabled() && ServerlessVideo && (
              <Route
                path="/inference/serverless/:id/video"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ServerlessVideo />
                  </React.Suspense>
                }
              />
            )}
            {isPodvirtInferenceEnabled() && GpuDashboard && (
              <Route
                path="/inference/gpu-dashboard"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <GpuDashboard />
                  </React.Suspense>
                }
              />
            )}
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </KYCProvider>
        </AccountFlaggedProvider>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#0D0D13',
            color: '#fff',
            borderRadius: '12px',
            border: '1px solid #232330',
          },
          success: {
            iconTheme: {
              primary: '#F97316',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
